import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { Organization, OrganizationsResponse } from '@/lib/types';

interface ListParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  /** False where the list only feeds a picker that is not on screen. */
  enabled?: boolean;
}

interface CreateOrganizationData {
  name: string;
  slug: string;
  isActive?: boolean;
  /** Who sold them their fleet; required before a device can be allocated in. */
  partnerId?: string | null;
}

export function useOrganizations(params: ListParams = {}) {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set('page', String(params.page));
  if (params.limit) queryParams.set('limit', String(params.limit));
  if (params.search) queryParams.set('search', params.search);
  if (params.isActive !== undefined) queryParams.set('isActive', String(params.isActive));

  const queryString = queryParams.toString();
  const endpoint = `/v1/admin/organizations${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['organizations', params],
    queryFn: () => apiFetch<OrganizationsResponse>(endpoint),
    enabled: params.enabled ?? true,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrganizationData) =>
      apiFetch<Organization>('/v1/admin/organizations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useOrganization(id: string) {
  return useQuery({
    queryKey: ['organizations', id],
    queryFn: () => apiFetch<Organization>(`/v1/admin/organizations/${id}`),
    enabled: Boolean(id),
  });
}

export function useUpdateOrganization(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<CreateOrganizationData>) =>
      apiFetch<Organization>(`/v1/admin/organizations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
  });
}
